import {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Button,
  CircularProgress,
  debounce,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import {
  getComplaints,
  getComplaintsMaster,
  getHpi,
  saveOrUpdateComplaints,
} from "../../services";
import { ComplaintContext } from ".";
import {
  complaintsDefaultVslues,
  ComplaintsDropdownType,
  HpiType,
  prepareComplaintsPayload,
} from "./types";
import usePopup from "../../../hooks/usePopup";
import { useParams } from "react-router-dom";
import { HL7ContexTriggerContex } from "../../../context/HL7MessageTrigger/HL7MessageTriggerProvider";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import MoodBadIcon from "@mui/icons-material/MoodBad";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { EmrContext } from "../../index";
const AddComplaints: FC<{ close?: () => void }> = ({ close }) => {
  const theme = useTheme();
  const { setTrigger } = useContext(HL7ContexTriggerContex);
  const { encounterId } = useParams();
  const { showSnackbar } = usePopup();
  const { triggerConsultationStart } = useContext(EmrContext);
  const [searchText, setSearchText] = useState<string>("");
  const [hpiSearchText, setHpiSearchText] = useState<string>("");
  const [complaints, setComplaints] = useState<ComplaintsDropdownType[]>([]);
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: complaintsDefaultVslues,
  });
  const { setList, edit, setEdit } = useContext(ComplaintContext);
  const allData = watch();
  const { mutate: fetchUpdatedComplaints } = useMutation(getComplaints, {
    onSuccess: (result) => {
      setList([...result.response]);
    },
  });

  const {
    mutate: fetchComplaints,
    // data: complaints,
    isLoading,
  } = useMutation(getComplaintsMaster, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        setComplaints(result.response);
      }
    },
  });

  const {
    mutate: fetchHpi,
    data: hpi,
    isLoading: hpiLoading,
  } = useMutation(getHpi);

  const { mutate: saveComplaint, isLoading: saveLoading } = useMutation(
    saveOrUpdateComplaints,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 2000,
          });
          setTrigger({
            encounterId: encounterId as string,
            tab: "complaints",
          });
          fetchUpdatedComplaints(encounterId as string);
          fetchComplaints("");
          setEdit(null);
          reset();
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
    },
  );

  const onSubmit = (value: any) => {
    let params = null;
    if (Boolean(edit)) {
      params = prepareComplaintsPayload({ ...edit, ...value, encounterId });
      setEdit(null);
    } else {
      params = prepareComplaintsPayload({ ...value, encounterId });
    }
    console.log(value);
    triggerConsultationStart();
    saveComplaint(params);
    // saveComplaint(params);
  };

  const handleClose = () => {
    close && close();
    setEdit(null);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case "complaints":
        setSearchText(value);
        fetchComplaints(value);
        break;
      case "hpi":
        setHpiSearchText(value);
        break;
    }
  };

  const debounceSearch = debounce(handleSearch, 500);

  const complaintsList = useMemo(() => {
    if (Boolean(complaints?.length)) {
      return complaints;
    } else {
      return [
        {
          comId: null,
          comName: searchText,
        },
      ];
    }
  }, [complaints, searchText]);

  const hpiList = useMemo(() => {
    const options = hpi?.response || [];
    const search = hpiSearchText.trim();
    const searchLower = search.toLowerCase();

    if (!search) {
      return options;
    }

    const filtered = options.filter((item) =>
      item.hpiName?.toLowerCase().includes(searchLower),
    );

    const hasExactMatch = options.some(
      (item) => item.hpiName?.toLowerCase() === searchLower,
    );

    if (!hasExactMatch) {
      const customOption: HpiType = {
        hpiId: null,
        hpiCode: "",
        hpiName: search,
      };
      return [...filtered, customOption];
    }

    return filtered.length > 0 ? filtered : options;
  }, [hpi?.response, hpiSearchText]);

  useEffect(() => {
    fetchComplaints("");
    fetchHpi();
  }, []);

  useEffect(() => {
    if (edit) {
      setHpiSearchText(edit.hpi?.hpiName || "");
      reset({
        ...edit,
        hpi: edit.hpi
          ? { ...edit.hpi, hpiName: edit.hpi.hpiName || "" }
          : null,
      });
    } else {
      setHpiSearchText("");
      reset({
        ...complaintsDefaultVslues,
      });
    }
  }, [edit, reset]);
  return (
    <div className="border-2 rounded">
      <div className="p-1 border-b  rounded-t" style={{}}>
        <Typography color={theme.palette.primary.main} fontWeight={600}>
          Add Complaints
        </Typography>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-2 grid grid-cols-3 gap-2"
      >
        <div className="col-span-full">
          <CustomFormField
            name="complaints"
            control={control}
            element="autocomplete"
            fieldProps={{ label: "Add complaints", onChange: debounceSearch }}
            rules={{ required: "Complaints required" }}
            options={complaintsList || []}
            loading={isLoading}
            autocompleteProps={{
              isOptionEqualToValue: (
                option: ComplaintsDropdownType,
                value: ComplaintsDropdownType,
              ) => option.comId == value.comId,
              getOptionLabel: (option: ComplaintsDropdownType) =>
                option.comName,
            }}
          />
        </div>
        <CustomFormField
          name="duration"
          control={control}
          element="input"
          fieldProps={{ label: "Duration" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="location"
          control={control}
          element="input"
          fieldProps={{ label: "Location" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="context"
          control={control}
          element="input"
          fieldProps={{ label: "Context" }}
          rules={{ required: false }}
        />

        <CustomFormField
          name="quality"
          control={control}
          element="input"
          fieldProps={{ label: "Quality" }}
          rules={{ required: false }}
        />
        {/* <CustomFormField
          name="severity"
          control={control}
          element="input"
          fieldProps={{ label: "Severity" }}
          rules={{ required: false }}
        /> */}
        <CustomFormField
          name="severity"
          control={control}
          element="autocomplete"
          options={[
            "None",
            "Mild",
            "Moderate",
            "Severe",
            "Very Severe",
            "Unbearable",
          ]}
          fieldProps={{ label: "Severity" }}
          rules={{ required: false }}
        />
        <div className="grid grid-cols-6 gap-2 justify-center items-center">
          <SentimentSatisfiedAltIcon
            sx={{ color: allData.severity == "None" ? "#4caf50" : "grey" }}
          />{" "}
          <SentimentSatisfiedIcon
            sx={{ color: allData.severity == "Mild" ? "#2e7d32" : "grey" }}
          />{" "}
          <SentimentDissatisfiedIcon
            sx={{ color: allData.severity == "Moderate" ? "#ff9800" : "grey" }}
          />{" "}
          <SentimentVeryDissatisfiedIcon
            sx={{ color: allData.severity == "Severe" ? "#e65100" : "grey" }}
          />{" "}
          <MoodBadIcon
            sx={{
              color: allData.severity == "Very Severe" ? "#e65100" : "grey",
            }}
          />
          <WarningAmberIcon
            sx={{
              color: allData.severity == "Unbearable" ? "#e65100" : "grey",
            }}
          />
        </div>
        <CustomFormField
          name="laterality"
          control={control}
          element="input"
          fieldProps={{ label: "Laterality" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="timing"
          control={control}
          element="input"
          fieldProps={{ label: "Timing" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="associatedSymptoms"
          control={control}
          element="input"
          fieldProps={{ label: "Associated Symptoms" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="diseaseRelation"
          control={control}
          element="input"
          fieldProps={{ label: "Disease Relation" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="encounterType"
          control={control}
          element="input"
          fieldProps={{ label: "Encounter Type" }}
          rules={{ required: false }}
        />
        <CustomFormField
          name="anatomicDetails"
          control={control}
          element="input"
          fieldProps={{ label: "Anatomic Details" }}
          rules={{ required: false }}
        />
        {/* <CustomFormField
          name="hip"
          control={control}
          element="input"
          fieldProps={{ label: "HIP" }}
          rules={{ required: false }}
        /> */}
        <Tooltip title={allData?.hpi?.hpiName || ""}>
          <div className="col-span-2">
            <CustomFormField
              name="hpi"
              control={control}
              element="autocomplete"
              fieldProps={{ label: "HPI", onChange: debounceSearch }}
              rules={{ required: false }}
              options={hpiList}
              loading={hpiLoading}
              autocompleteProps={{
                filterOptions: (options) => options,
                isOptionEqualToValue: (option: HpiType, value: HpiType) =>
                  option.hpiId != null && value.hpiId != null
                    ? option.hpiId === value.hpiId
                    : option.hpiName === value.hpiName,
                getOptionLabel: (option: HpiType) => option.hpiName || "",
              }}
            />
          </div>
        </Tooltip>
        <div className="flex justify-end items-center gap-3">
          <Button
            type="submit"
            color="success"
            disabled={saveLoading}
            endIcon={
              saveLoading && (
                <CircularProgress
                  size={18}
                  sx={{ color: theme.palette.background.paper }}
                />
              )
            }
          >
            {" "}
            {Boolean(edit) ? "update" : "add"}{" "}
          </Button>
          <Button color="error" onClick={handleClose}>
            {" "}
            close{" "}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddComplaints;
